import React, { useEffect, useMemo, useState } from "react";
import Navbar from "../../../../common/layout/Navbar";
import { LicenseTypes } from "../../../../../utils/Enum.types";
import { Title } from "../../../../../stories/Title/Title";
import { Button } from "../../../../../stories/Button/Button";
import { useNavigate } from "react-router-dom";
import useUrlParams from "../../../../../customHooks/useUrlParams";
import { useLazyQuery } from "@apollo/client";
import { GetUserRights } from "./queries/queries";
import {
  GetUserRightsResult,
  GetUserRightsVar,
  UserRightsMasterEdge,
  UserRightsMasterOrderField
} from "./queries/types";

import { OrderDirection } from "../../../../../utils/commonQueryTypes";
import { FETCH_MORE_DATA, ROWS_PER_PAGE } from "../../../../../utils/constants";
import LoadingModal from "../../../../pages/LoadingModal";

import { getModifiedScrollHeight } from "../../../../../utils/UtilFunctions";
import useAssetTableJson from "../../../../../json/useAssetTableJson";
import {
  GridAlignment,
  GridColDef,
} from "@mui/x-data-grid-pro";
import { TableHeaderProps } from "../../../../../utils/types";
import { StyledDatagrid } from "../../../../../styles/DatagridStyles";
import { TABLE_ROW_HEIGHT } from "../../../../../styles/Variables";

const AssignedList = () => {
  const navigate = useNavigate();

  const { customer_id } = useUrlParams();
  const { Users } = useAssetTableJson();
  const [hasNextPage, setHasNextPage] = useState<boolean>(true);
  const [employees, setEmployees] = useState<UserRightsMasterEdge[]>([]);
  const [endCursor, setEndCursor] = useState<string | null>(null);

  const [getUserRights, { data, loading, fetchMore }] = useLazyQuery<
    GetUserRightsResult,
    GetUserRightsVar
  >(GetUserRights);


  useEffect(() => {
    const scrollTable = document.getElementsByClassName(
      "MuiDataGrid-virtualScroller"
    )[0] as Element;
    const handleScroll = (e: Event) => {
      const target = e.target as HTMLDivElement;
      const scrollTop = target.scrollTop;
      const scrollHeight = target.scrollHeight;
      const clientHeight = target.clientHeight;
      if (scrollTop + clientHeight >= getModifiedScrollHeight(scrollHeight)) {
        if (hasNextPage && !loading) {
          fetchMore({
            variables: {
              first: FETCH_MORE_DATA,
              after: endCursor,
            },
            updateQuery: (prevResult, { fetchMoreResult }) => {
              if (!fetchMoreResult) return prevResult;

              const newEdges = fetchMoreResult.GetUserRights.edges;
              const pageInfo = fetchMoreResult.GetUserRights.pageInfo;
              setEndCursor(pageInfo.endCursor);
              setHasNextPage(pageInfo.hasNextPage);

              const duplicateCheck = prevResult.GetUserRights.edges.filter(
                ({ node: { id } }) =>
                  newEdges.findIndex(
                    ({ node: { id: newId } }) => newId === id
                  ) !== -1
              );

              if (duplicateCheck.length > 0) return prevResult;

              return {
                GetUserRights: {
                  edges: [...employees, ...newEdges],
                  pageInfo,
                  totalCount: data ? data.GetUserRights.totalCount! : 0,
                },
              };
            },
          });
        }
      }
    };
    if (scrollTable && employees.length)
      scrollTable.addEventListener("scroll", handleScroll);

    return () => {
      if (scrollTable) scrollTable.removeEventListener("scroll", handleScroll);
    };
  }, [employees]);
  useEffect(() => {
    if (data && !loading) {
      const newData = data.GetUserRights.edges.map((edge) => ({
        ...edge,
        node: {
          ...edge.node,
          isChecked: true // set default value of isChecked to true
        }
      }));

      if (endCursor) {
        const updatedNewData = newData.map((newEmp) => {
          const filteredStudent = employees.find(
            (emp) => emp.node.empDetails.id === newEmp.node.empDetails.id
          );
          if (filteredStudent) {
            return {
              ...newEmp,
              node: {
                ...newEmp.node
              }
            };
          }
          return newEmp;
        });
        setEmployees(updatedNewData);
      } else {
        setEmployees(newData);
      }
      setEndCursor(data.GetUserRights.pageInfo.endCursor);
    } // eslint-disable-next-line
  }, [data, loading]);

  useEffect(() => {
    getUserRights({
      variables: {
      
        customerID: customer_id ? customer_id : 0,
        after: null,
        first: ROWS_PER_PAGE,
        orderBy: {
          direction: OrderDirection.ASC,
          field: UserRightsMasterOrderField.EMP_ID
        }
      }
    });
  }, [getUserRights]);

  const dynamicHeaders: TableHeaderProps[] = Users.Configurations.map(
    (header) => ({
      headerName: header.headerName,
      cellClassName: header.cellClassName,
      field: header.field,
      headerAlign: header.headerAlign as GridAlignment,
      align: header.align as GridAlignment,
      flex: header.flex,
      hideable: header.hideable
    })
  );
  const columns: GridColDef[] = [...dynamicHeaders];
  const rowsData = useMemo(() => {
    return employees.map((emp, index) => ({
      id: index + 1,
      emp_id: emp.node.empDetails.id,
      emp_name: emp.node.empDetails.name,
      mobile: emp.node.empDetails.mobile,
      user_type: emp.node.empDetails.empType,
      tableLevel: emp.node.tableLevel
    }));
  }, [employees]);

  return (
    <>
      <Navbar NavType={LicenseTypes.CUSTOMER_USERS} />

      <Title>User Rights Configurations</Title>
      <div className="user-configuration">
        <div className="row g-0 user-configuration__filters">
          <div className="col"></div>
          <div className="col-2 flex-end">
            <Button
              onClick={() =>
                navigate(`/${customer_id}/company/users/0/assignrights`)
              }
              mode="addnew"
              autoFocus
            />
          </div>
        </div>
        <div className="user-configuration__tableblock">
          <StyledDatagrid
            columns={columns}
            rows={rowsData}
            rowHeight={TABLE_ROW_HEIGHT}
            disableRowSelectionOnClick
            hideFooter
            onCellClick={(params) => {       
              if (params.field === "emp_name") {
                navigate(
                  `/${customer_id}/company/users/${params.row.emp_id}/assignrights`
                );
              }
            }}
          />
        </div>
        <Button mode="back" onClick={() => navigate(-1)} />
      </div>
      <LoadingModal flag={loading} />
    </>
  );
};

export default AssignedList;
